import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import AuthGuard from "../components/auth-guard";
import { apiRequest } from "../lib/queryClient";
import ErrorModal from "../components/error-modal";
import { QuestionnaireDocument, type QuestionnaireJsonData } from "../components/QuestionnaireDocument";
import { InstructionPreviewBody } from "../components/InstructionPreviewBody";
import { useIsMobile } from "../hooks/use-mobile";
import { STUDENT_HEADER_HEIGHT } from "../components/student/StudentHeader";

// ===== Design tokens =====
const COLORS = {
  bg: "#FAFAF7",
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  line: "#E4E4E7",
  paper: "#FDFCF7",
  paperLine: "#ECE5D4",
};

type Difficulty = "易" | "中" | "難";
type GuidanceLevel = "detailed" | "viewpoints" | "purpose_only";

interface GuidanceOption {
  level: GuidanceLevel;
  label: string;
  summary: string | null;
  tasks: Array<{ no: number; title: string; details?: string[] }>;
}
const DIFFICULTY_META: Record<
  Difficulty,
  { label: string; sub: string; color: string; soft: string; dots: string }
> = {
  易: { label: "やさしい", sub: "基本聴取", color: "#0E7490", soft: "#ECFEFF", dots: "●○○" },
  中: { label: "ふつう", sub: "行動変容なし", color: "#7C3AED", soft: "#F5F3FF", dots: "●●○" },
  難: { label: "むずかしい", sub: "行動変容あり", color: "#BE123C", soft: "#FFF1F2", dots: "●●●" },
};

interface Scenario {
  id: number;
  scenarioKey?: string;
  guidanceLevel?: "detailed" | "viewpoints" | "purpose_only" | null;
  guidanceLabel?: string | null;
  guidanceOptions?: GuidanceOption[];
  conditionId: number;
  mode: string;
  hasBehaviorChange: boolean;
  gender: string;
  ageRange: string;
  summary: string | null;
  targetCategories: string[];
  /**
   * サーバー側で condition_data.categoryDefinitions[].displayName から解決した
   * カテゴリID → 表示名 マップ。古い固定カテゴリ名を上書きする。
   */
  targetCategoryLabels?: Record<string, string>;
  tasks: Array<{ no: number; title: string; details?: string[] }>;
  questionnaire: string | null;
  questionnaireJson: QuestionnaireJsonData | null;
  instruction: Record<string, unknown> | null;
}

function difficultyOf(s: Scenario): Difficulty {
  if (s.mode === "intake") return "易";
  return s.hasBehaviorChange ? "難" : "中";
}

function parseStartInterviewErrorMessage(error: unknown): string {
  let message = "予期せぬエラーが発生しました";
  if (error && typeof error === "object" && "message" in error && typeof (error as { message?: string }).message === "string") {
    const errMsg = (error as { message: string }).message;
    const jsonMatch = errMsg.match(/\d+:\s*(\{[\s\S]*\})/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[1]) as { message?: string };
        if (parsed.message) return parsed.message;
      } catch {
        return errMsg;
      }
    }
    return errMsg;
  }
  return message;
}

function parseGuidanceLevel(value: string | null): GuidanceLevel {
  if (value === "viewpoints" || value === "purpose_only") return value;
  return "detailed";
}

export default function ScenarioPreview() {
  return (
    <AuthGuard>
      <ScenarioPreviewContent />
    </AuthGuard>
  );
}

function ScenarioPreviewContent() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [previewStep, setPreviewStep] = useState<2 | 3>(2);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const isMobile = useIsMobile();

  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const instructionId = Number(params.get("instructionId") ?? "") || null;
  const [selectedGuidanceLevel, setSelectedGuidanceLevel] = useState<GuidanceLevel>(() =>
    parseGuidanceLevel(params.get("guidanceLevel")),
  );
  const previewUrl = instructionId ? `/api/scenarios/${instructionId}/preview` : "";

  const { data, isLoading } = useQuery<{ scenario: Scenario; avatar: { id: number; name: string; gender: string; age: number; birthDate?: string | null } }>({
    queryKey: [previewUrl],
    enabled: instructionId !== null,
    staleTime: Infinity,
  });

  const baseScenario = data?.scenario ?? null;
  const selectedAvatar = data?.avatar ?? null;
  const selectedGuidance = baseScenario?.guidanceOptions?.find(
    (option) => option.level === selectedGuidanceLevel,
  ) ?? baseScenario?.guidanceOptions?.[0] ?? null;
  const scenario = baseScenario
    ? {
        ...baseScenario,
        guidanceLevel: selectedGuidance?.level ?? baseScenario.guidanceLevel,
        guidanceLabel: selectedGuidance?.label ?? baseScenario.guidanceLabel,
        summary: baseScenario.summary,
        tasks: selectedGuidance?.tasks ?? baseScenario.tasks,
      }
    : null;

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!scenario || !selectedAvatar) throw new Error("シナリオが見つかりません");
      const response = await apiRequest("POST", "/api/start-interview", {
        instructionId: scenario.id,
        guidanceLevel: scenario.guidanceLevel ?? undefined,
        avatarId: selectedAvatar.id,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
      setLocation("/interview");
    },
    onError: (error: unknown) => {
      setErrorMessage(parseStartInterviewErrorMessage(error));
      setIsErrorOpen(true);
    },
  });

  const goBack = () => {
    if (previewStep === 3) {
      setPreviewStep(2);
      return;
    }
    setLocation("/select-filter");
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 top-[68px] flex items-center justify-center" style={{ background: COLORS.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.muted }} />
      </div>
    );
  }

  if (!scenario || !selectedAvatar) {
    return (
      <div className="fixed inset-0 top-[68px] flex flex-col items-center justify-center gap-4" style={{ background: COLORS.bg }}>
        <p style={{ color: COLORS.muted }}>シナリオが見つかりません</p>
        <button
          onClick={() => setLocation("/select-filter")}
          style={{
            padding: "8px 16px", border: `1px solid ${COLORS.line}`, background: COLORS.surface,
            borderRadius: 8, fontSize: 13, fontWeight: 600, color: COLORS.ink,
            cursor: "pointer",
          }}
        >
          ← シナリオ選択に戻る
        </button>
      </div>
    );
  }

  const d = difficultyOf(scenario);
  const meta = DIFFICULTY_META[d];

  if (isMobile) {
    return (
      <div
        className="fixed inset-0 flex flex-col overflow-hidden"
        style={{
          top: STUDENT_HEADER_HEIGHT,
          background: COLORS.bg,
          color: COLORS.ink,
          fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        }}
      >
        <div
          style={{
            flex: "0 0 auto",
            padding: "14px 20px 16px",
            background: COLORS.surface,
            borderBottom: `1px solid ${COLORS.line}`,
            position: "relative",
          }}
        >
          <button
            type="button"
            onClick={goBack}
            style={{
              position: "absolute",
              left: 18,
              top: 14,
              padding: "6px 10px",
              border: `1px solid ${COLORS.line}`,
              background: COLORS.surface,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              color: COLORS.ink,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            ← 戻る
          </button>
          <div style={{ textAlign: "center", padding: "0 60px" }}>
            <div
              style={{
                fontSize: 10,
                color: COLORS.muted,
                fontWeight: 800,
                letterSpacing: "0.15em",
                marginBottom: 8,
              }}
            >
              {previewStep === 2 ? "STEP 02・課題文選択" : "STEP 03・問診票確認"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.4, lineHeight: 1.25 }}>
              {previewStep === 2 ? "課題文を選ぶ" : "問診票を確認"}
            </div>
          </div>
        </div>

        <div
          style={{
            flex: "0 0 auto",
            padding: "14px 20px",
            background: "#FAF7EF",
            borderBottom: `1px solid ${COLORS.line}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 999,
              background: "#E8DFC4",
              color: COLORS.ink,
              display: "grid",
              placeItems: "center",
              fontSize: 16,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {selectedAvatar.name.slice(0, 1)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 18, fontWeight: 800 }}>{selectedAvatar.name}</span>
              <span style={{ fontSize: 12.5, color: COLORS.muted, fontWeight: 700 }}>
                {selectedAvatar.age}歳・{scenario.gender}
              </span>
            </div>
            <div
              style={{
                marginTop: 5,
                fontSize: 12.5,
                color: "#4B4738",
                lineHeight: 1.55,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {scenario.summary || "（シナリオ概要は未設定）"}
            </div>
          </div>
          <span
            style={{
              padding: "5px 9px",
              borderRadius: 8,
              background: meta.soft,
              color: meta.color,
              fontSize: 12,
              fontWeight: 800,
              border: `1px solid ${meta.color}`,
              flexShrink: 0,
            }}
          >
            {d}
          </span>
        </div>

        {previewStep === 2 && scenario.guidanceOptions && scenario.guidanceOptions.length > 1 && (
          <div style={{ flex: "0 0 auto", padding: "12px 20px 0", background: COLORS.bg }}>
            <GuidanceLevelSelector
              options={scenario.guidanceOptions}
              value={selectedGuidanceLevel}
              compact
              onChange={setSelectedGuidanceLevel}
            />
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px 104px" }}>
          {previewStep === 2 ? (
            <InstructionView scenario={scenario} />
          ) : (
            <QuestionnaireView scenario={scenario} avatar={selectedAvatar} compact />
          )}
        </div>

        <button
          type="button"
          onClick={() => previewStep === 2 ? setPreviewStep(3) : startMutation.mutate()}
          disabled={previewStep === 3 && startMutation.isPending}
          style={{
            position: "absolute",
            right: 20,
            bottom: "max(18px, env(safe-area-inset-bottom))",
            minHeight: 64,
            padding: "0 22px",
            borderRadius: 999,
            background: previewStep === 3 && startMutation.isPending
              ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
              : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
            color: "#FFFFFF",
            border: "none",
            boxShadow: previewStep === 3 && startMutation.isPending
              ? "0 2px 6px rgba(0,0,0,0.12)"
              : "0 0 0 4px rgba(255,138,61,0.15), 0 10px 28px rgba(255,107,26,0.42), 0 2px 6px rgba(0,0,0,0.12)",
            cursor: previewStep === 3 && startMutation.isPending ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "inherit",
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: 0.3,
          }}
        >
          {previewStep === 3 && startMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              開始中...
            </>
          ) : previewStep === 2 ? (
            <>問診票の確認へ →</>
          ) : (
            <>STEP 04 面接を開始 →</>
          )}
        </button>

        <ErrorModal isOpen={isErrorOpen} onClose={() => setIsErrorOpen(false)} message={errorMessage} />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 top-[68px] flex flex-col overflow-hidden"
      style={{
        background: COLORS.bg,
        color: COLORS.ink,
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          padding: "20px 32px 24px",
          background: COLORS.surface,
          borderBottom: `1px solid ${COLORS.line}`,
          display: "flex",
          alignItems: "center",
          gap: 16,
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={goBack}
          style={{
            padding: "6px 12px",
            border: `1px solid ${COLORS.line}`,
            background: COLORS.surface,
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: COLORS.ink,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ← 戻る
        </button>
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: COLORS.muted,
              fontWeight: 700,
              letterSpacing: 1.5,
              marginBottom: 6,
            }}
          >
            {previewStep === 2 ? "STEP 02 · 課題文選択" : "STEP 03 · 問診票確認"}
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.2 }}>
            {previewStep === 2 ? "課題文を選ぶ" : "問診票を確認"}
          </div>
        </div>
      </div>

      {/* Patient summary strip */}
      <div
        style={{
          padding: "14px 32px",
          background: "#FAF7EF",
          borderBottom: `1px solid ${COLORS.line}`,
          display: "flex",
          alignItems: "center",
          gap: 20,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            background: "#E8DFC4",
            display: "grid",
            placeItems: "center",
            fontSize: 18,
          }}
        >
          👤
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{selectedAvatar.name}</div>
            <div style={{ fontSize: 14, color: COLORS.muted, fontWeight: 600 }}>
              {selectedAvatar.age}歳・{scenario.gender}
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#4B4738", lineHeight: 1.6 }}>
            {scenario.summary || "（シナリオ概要は未設定）"}
          </div>
        </div>
        <span
          style={{
            padding: "4px 10px",
            borderRadius: 6,
            background: meta.soft,
            color: meta.color,
            fontSize: 11.5,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <span style={{ fontFamily: "system-ui", fontSize: 9, letterSpacing: 1.5 }}>{meta.dots}</span>
          {d} · {meta.sub}
        </span>
      </div>

      {previewStep === 2 && scenario.guidanceOptions && scenario.guidanceOptions.length > 1 && (
        <div style={{ padding: "14px 32px", background: COLORS.bg, borderBottom: `1px solid ${COLORS.line}` }}>
          <GuidanceLevelSelector
            options={scenario.guidanceOptions}
            value={selectedGuidanceLevel}
            onChange={setSelectedGuidanceLevel}
          />
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px 100px" }}>
        {previewStep === 2 ? (
          <InstructionView scenario={scenario} />
        ) : (
          <QuestionnaireView scenario={scenario} avatar={selectedAvatar} />
        )}
      </div>

      {/* Orange FAB */}
      <button
        type="button"
        onClick={() => previewStep === 2 ? setPreviewStep(3) : startMutation.mutate()}
        disabled={previewStep === 3 && startMutation.isPending}
        style={{
          position: "absolute",
          right: 24,
          bottom: 24,
          padding: "14px 22px",
          borderRadius: 999,
          background: previewStep === 3 && startMutation.isPending
            ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
            : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
          color: "#fff",
          border: "none",
          boxShadow: previewStep === 3 && startMutation.isPending
            ? "0 2px 6px rgba(0,0,0,0.12)"
            : "0 0 0 4px rgba(255,138,61,0.15), 0 10px 28px rgba(255,107,26,0.45), 0 2px 6px rgba(0,0,0,0.12)",
          cursor: previewStep === 3 && startMutation.isPending ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontFamily: "inherit",
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 0.3,
          transition: "transform 150ms ease-out",
        }}
        onMouseEnter={(e) => {
          if (previewStep === 3 && startMutation.isPending) return;
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {previewStep === 3 && startMutation.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            開始中...
          </>
        ) : previewStep === 2 ? (
          <>問診票の確認へ →</>
        ) : (
          <>STEP 04 面接を開始 →</>
        )}
      </button>

      <ErrorModal isOpen={isErrorOpen} onClose={() => setIsErrorOpen(false)} message={errorMessage} />
    </div>
  );
}

const GUIDANCE_PRESENTATION: Record<GuidanceLevel, { title: string; description: string }> = {
  detailed: { title: "初級", description: "聞く内容を具体的に表示" },
  viewpoints: { title: "中級", description: "確認する観点を表示" },
  purpose_only: { title: "上級", description: "面接の目的だけを表示" },
};

function GuidanceLevelSelector({
  options,
  value,
  onChange,
  compact = false,
}: {
  options: GuidanceOption[];
  value: GuidanceLevel;
  onChange: (level: GuidanceLevel) => void;
  compact?: boolean;
}) {
  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={{ marginBottom: 7, fontSize: 12, fontWeight: 800, color: COLORS.ink }}>
        初級・中級・上級から課題文を選択
      </div>
      <div
        role="group"
        aria-label="課題文のレベル"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          border: `1px solid ${COLORS.line}`,
          borderRadius: 8,
          overflow: "hidden",
          background: COLORS.surface,
        }}
      >
        {options.map((option, index) => {
          const selected = option.level === value;
          const presentation = GUIDANCE_PRESENTATION[option.level];
          return (
            <button
              key={option.level}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.level)}
              style={{
                minHeight: compact ? 54 : 62,
                padding: compact ? "8px 6px" : "9px 14px",
                border: "none",
                borderLeft: index > 0 ? `1px solid ${COLORS.line}` : "none",
                background: selected ? "#ECFEFF" : COLORS.surface,
                color: selected ? "#0E7490" : COLORS.ink,
                boxShadow: selected ? "inset 0 -3px 0 #0E7490" : "none",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "center",
              }}
            >
              <span style={{ display: "block", fontSize: compact ? 13 : 14, fontWeight: 800 }}>
                {presentation.title}
              </span>
              {!compact && (
                <span style={{ display: "block", marginTop: 3, fontSize: 11, color: selected ? "#0E7490" : COLORS.muted }}>
                  {presentation.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuestionnaireView({
  scenario,
  avatar,
  compact = false,
}: {
  scenario: Scenario;
  avatar: { name: string; age: number; gender: string; birthDate?: string | null };
  compact?: boolean;
}) {
  // JSON があればデザイン版の QuestionnaireDocument で描画。
  if (scenario.questionnaireJson) {
    return (
      <QuestionnaireDocument
        json={scenario.questionnaireJson}
        patientName={avatar.name}
        meta={{ age: avatar.age, gender: scenario.gender, birthDate: avatar.birthDate }}
        compact={compact}
      />
    );
  }

  // JSON がない旧データは従来の紙風カード + テキスト描画にフォールバック
  return (
    <div
      style={{
        maxWidth: 960,
        margin: "0 auto",
        background: COLORS.paper,
        border: `1px solid ${COLORS.paperLine}`,
        borderRadius: 12,
        padding: compact ? "22px 16px" : "36px 44px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        overflowX: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: 16,
          marginBottom: 20,
          borderBottom: `2px solid ${COLORS.paperLine}`,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: "#8A7E5F",
              fontWeight: 700,
              letterSpacing: 1.5,
              marginBottom: 3,
            }}
          >
            MEDICAL INTERVIEW SHEET
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.3 }}>
            問診票
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: 11, color: "#6B6144" }}>
          <div>{avatar.name}</div>
          <div style={{ fontSize: 10 }}>
            {avatar.age}歳・{scenario.gender}
          </div>
        </div>
      </div>
      {scenario.questionnaire ? (
        <QuestionnaireBody text={scenario.questionnaire} />
      ) : (
        <p style={{ fontSize: 13, color: COLORS.muted }}>問診票が未設定です。問診票を生成してください。</p>
      )}
    </div>
  );
}

/**
 * 問診票テキストをレンダリング。
 * 「歯科問診票」のタイトル行 + その後の説明文2行を一段大きく / 中央寄せで
 * 上下1行分の余白を持たせて表示する。
 */
function QuestionnaireBody({ text }: { text: string }) {
  const preStyle: React.CSSProperties = {
    whiteSpace: "pre-wrap",
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
    fontFamily:
      '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif',
    fontSize: 14.5,
    lineHeight: 2,
    letterSpacing: "0.02em",
    color: "#18181B",
    margin: 0,
  };

  const lines = text.split("\n");
  const titleIdx = lines.findIndex((l) => l.trim() === "歯科問診票");

  if (titleIdx < 0) {
    return <pre style={preStyle}>{text}</pre>;
  }

  // タイトル直後の説明文(インデント付き)が続く範囲を探す。
  // 次に [ で始まるセクション行（[１] 等）が来たら終わり。
  let descEnd = titleIdx + 1;
  while (descEnd < lines.length) {
    const trimmed = lines[descEnd].trim();
    if (trimmed.startsWith("[")) break;
    descEnd++;
  }

  const before = lines.slice(0, titleIdx).join("\n");
  const descLines = lines
    .slice(titleIdx + 1, descEnd)
    .map((l) => l.trim())
    .filter(Boolean);
  const after = lines.slice(descEnd).join("\n");

  return (
    <>
      {before.length > 0 && <pre style={preStyle}>{before}</pre>}
      <div
        style={{
          padding: "2em 0",
          textAlign: "center",
          fontFamily: preStyle.fontFamily,
          color: "#18181B",
          letterSpacing: "0.05em",
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            marginBottom: descLines.length > 0 ? 10 : 0,
            letterSpacing: "0.15em",
          }}
        >
          歯科問診票
        </div>
        {descLines.length > 0 && (
          <div
            style={{
              display: "inline-block",
              textAlign: "left",
              fontSize: 13.5,
              lineHeight: 1.9,
              color: "#3F3F46",
            }}
          >
            {descLines.map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>
        )}
      </div>
      {after.length > 0 && <pre style={preStyle}>{after}</pre>}
    </>
  );
}

function InstructionView({ scenario }: { scenario: Scenario }) {
  const levelTitle = scenario.guidanceLevel
    ? GUIDANCE_PRESENTATION[scenario.guidanceLevel].title
    : null;

  return (
    <div>
      {levelTitle && (
        <div
          style={{
            display: "inline-flex",
            marginBottom: 14,
            padding: "5px 11px",
            borderRadius: 999,
            border: "1px solid #0E7490",
            background: "#ECFEFF",
            color: "#0E7490",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          課題文: {levelTitle}
        </div>
      )}
      <InstructionPreviewBody
        summary={scenario.summary}
        targetCategories={scenario.targetCategories}
        categoryLabels={scenario.targetCategoryLabels}
        tasks={scenario.tasks}
      />
    </div>
  );
}
